
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  name: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /**
   * True between clicking an emailed password-recovery link and setting a new
   * password. Supabase exchanges the recovery token for a real session, so the
   * user looks signed in — the app must still force the reset screen.
   */
  isPasswordRecovery: boolean;
  /** Leaves recovery mode once the new password has been saved. */
  clearPasswordRecovery: () => void;
}

/**
 * Survives the remount that Supabase's token exchange can trigger, so the
 * recovery flag isn't lost between the redirect landing and React settling.
 */
const RECOVERY_FLAG_KEY = 'lmm.passwordRecovery';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(
    () => sessionStorage.getItem(RECOVERY_FLAG_KEY) === 'true'
  );

  const clearPasswordRecovery = () => {
    sessionStorage.removeItem(RECOVERY_FLAG_KEY);
    setIsPasswordRecovery(false);
  };

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (!error && data) {
      setProfile(data);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Fires when the emailed recovery link is exchanged for a session.
        // Latch it so the reset screen is shown instead of the app.
        if (event === 'PASSWORD_RECOVERY') {
          sessionStorage.setItem(RECOVERY_FLAG_KEY, 'true');
          setIsPasswordRecovery(true);
        }

        // An explicit sign-out abandons an in-progress recovery.
        if (event === 'SIGNED_OUT') {
          sessionStorage.removeItem(RECOVERY_FLAG_KEY);
          setIsPasswordRecovery(false);
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          Sentry.setUser({
            id: session.user.id,
            email: session.user.email,
          });
          fetchProfile(session.user.id);
        } else {
          Sentry.setUser(null);
          setProfile(null);
        }
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        Sentry.setUser({
          id: session.user.id,
          email: session.user.email,
        });
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, profile, loading, signOut, isPasswordRecovery, clearPasswordRecovery }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
